import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { toast } from 'sonner';
import { privateApi } from '../../../api/api';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import CurrencyInput from '../../../shared/components/form/CurrencyInput';
import Select from 'react-select';
import { LinkRateSpecificRule } from '../types/LinkRateSpecificRule';
import { LinkRateSpecificAmounType } from '../types/LinkRateSpecificAmounType';
import { CreateLinkRateSchema } from '../schema/CreateLinkRateSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation';

interface CreateLinkRateDialogProps {
    show: boolean;
    onClose: () => void;
    rateId: string;
}

const CreateLinkRateDialog: React.FC<CreateLinkRateDialogProps> = ({ show, onClose, rateId }) => {
    const { register, handleSubmit, formState: { errors }, reset, control, watch, setError } = useForm({
        resolver: zodResolver(CreateLinkRateSchema),
    });

    const inheritRestriction = watch('inherit_restriction');

    useEffect(() => {
        if (inheritRestriction === true) {
            reset((prev) => ({
                ...prev,
                inherit_closed_to_arrival: true,
                inherit_closed_to_departure: true,
                inherit_stop_sell: true,
                inherit_min_stay_arrival: true,
                inherit_min_stay_through: true,
                inherit_max_stay: true,
            }));
        }
    }, [inheritRestriction, reset]);

    useEffect(() => {
        if (show) {
            reset({
                inherit_restriction: true,
                inherit_closed_to_arrival: true,
                inherit_closed_to_departure: true,
                inherit_stop_sell: true,
                inherit_min_stay_arrival: true,
                inherit_min_stay_through: true,
                inherit_max_stay: true,
            });
        }
    }, [show, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const { data: rate } = useQuery({
        queryKey: ['rate', rateId],
        queryFn: () => privateApi.get(`/v4/rate-plan/${rateId}`).then((res) => res.data.data),
        enabled: !!rateId
    })

    useEffect(() => {
        if (rate) {
            reset((prev) => ({
                ...prev,
                max_night: rate.max_night,
                min_night: rate.min_night,
                guest_include: rate.guest_include,
                guest_max: rate.guest_max,
                extra_adult: rate.extra_adult,
                extra_child: rate.extra_child,
                extra_adult_rate: rate.extra_adult_rate,
                extra_child_rate: rate.extra_child_rate,
            }));
        }
    }, [rate]);

    if (rate?.isError) {
        toast.error('Something went wrong, Failed to fetch rate', {
            id: 'create-link-rate'
        })
        handleClose()
    }

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (data: any) => {
            return privateApi.post('/v4/link-rate-plan', data);
        },
        onSuccess: async (data) => {
            reset();

            handleClose();

            await queryClient.refetchQueries({
                predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates'].includes(key as string))
            });

            toast.success(`Rate ${data.data.data.name} as Link created successfully`, {
                id: 'create-link-rate'
            });
        },
        onError: (error: any) => {
            const handled = applyServerValidationErrors(error, setError, {
                toastId: 'create-link-rate',
            });

            if (!handled) {
                toast.error(error?.response?.data?.message || 'Failed to create rate', {
                    id: 'create-link-rate'
                });
            }
        }
    })

    const onSubmit = useCallback((data: any) => {
        toast.loading('Creating link rate...', { id: 'create-link-rate' });
        const payload = {
            ...data,
            rate_plan_id: rateId,
            specific_amount_type: data.specific_amount_type.value,
            specific_rule: data.specific_rule.value,
            inherit_restriction: data.inherit_restriction ? 1 : 0
        };
        mutate(payload)
    }, [mutate, rateId])

    return (
        <Offcanvas show={show} onHide={onClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Link Rate</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='row mb-3 align-items-center'>
                        <label htmlFor='rate_id' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Rate Parent: </label>
                        <div className='col-sm-8'>
                            <span className='badge bg-primary text-white rounded-1'>{rate?.name}</span>
                        </div>
                    </div>

                    <div className='row mb-3 align-items-center'>
                        <label htmlFor='inherit_restriction' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Inherit Restriction: </label>
                        {/* Switch button */}
                        <div className='col-sm-8'>
                            <div className='form-check form-switch'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    id='inherit_restriction'
                                    {...register('inherit_restriction')}
                                />
                                <label className='form-check-label' htmlFor='inherit_restriction'></label>
                            </div>
                        </div>
                    </div>

                    {/* Name */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Link Rate Name: </label>
                        <div className="col-sm-8">
                            <input
                                type="text"
                                className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                id="name"
                                placeholder='Enter rate name'
                                {...register('name')}
                            />
                            {errors?.name && (
                                <div className="invalid-feedback">{typeof errors.name.message === 'string' && errors.name.message}</div>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Description: </label>
                        <div className="col-sm-8">
                            <textarea
                                className={clsx("form-control app-form-control rounded-1", errors?.description && 'is-invalid')}
                                id="description"
                                placeholder='Enter rate description'
                                {...register('description')}
                            />
                            {errors?.description && (
                                <div className="invalid-feedback">{typeof errors.description.message === 'string' && errors.description.message}</div>
                            )}
                        </div>
                    </div>
                    <h3 className="mt-6 border-bottom fw-normal">Rate Settings</h3>
                    {/* Max Night */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="max_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Max Night: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.max_night && 'is-invalid')}
                                id="max_night"
                                placeholder='Enter max night'
                                disabled={inheritRestriction}
                                {...register('max_night')}
                            />
                            {errors?.max_night && (
                                <div className="invalid-feedback">{typeof errors.max_night.message === 'string' && errors.max_night.message}</div>
                            )}
                        </div>
                    </div>

                    {/* Min Night */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="min_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Night: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.min_night && 'is-invalid')}
                                id="min_night"
                                placeholder='Enter min night'
                                disabled={inheritRestriction}
                                {...register('min_night')}
                            />
                            {errors?.min_night && (
                                <div className="invalid-feedback">{typeof errors.min_night.message === 'string' && errors.min_night.message}</div>
                            )}
                        </div>
                    </div>

                    {/* guest_include */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="guest_include" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Include: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.guest_include && 'is-invalid')}
                                id="guest_include"
                                placeholder='Enter guest include'
                                disabled={inheritRestriction}
                                {...register('guest_include')}
                            />
                            {errors?.guest_include && (
                                <div className="invalid-feedback">{typeof errors.guest_include.message === 'string' && errors.guest_include.message}</div>
                            )}
                        </div>
                    </div>

                    {/* guest_max */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="guest_max" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Max: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.guest_max && 'is-invalid')}
                                id="guest_max"
                                placeholder='Enter guest max'
                                disabled={inheritRestriction}
                                {...register('guest_max')}
                            />
                            {errors?.guest_max && (
                                <div className="invalid-feedback">{typeof errors.guest_max.message === 'string' && errors.guest_max.message}</div>
                            )}
                        </div>
                    </div>

                    {/* extra_adult */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_adult" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.extra_adult && 'is-invalid')}
                                id="extra_adult"
                                placeholder='Enter extra adult'
                                disabled={inheritRestriction}
                                {...register('extra_adult')}
                            />
                            {errors?.extra_adult && (
                                <div className="invalid-feedback">{typeof errors.extra_adult.message === 'string' && errors.extra_adult.message}</div>
                            )}
                        </div>
                    </div>

                    {/* extra_child */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_child" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.extra_child && 'is-invalid')}
                                id="extra_child"
                                placeholder='Enter extra child'
                                disabled={inheritRestriction}
                                {...register('extra_child')}
                            />
                            {errors?.extra_child && (
                                <div className="invalid-feedback">{typeof errors.extra_child.message === 'string' && errors.extra_child.message}</div>
                            )}
                        </div>
                    </div>

                    {watch('inherit_restriction') !== true && (
                        <>
                            <h3 className="mt-6 border-bottom fw-normal">Restrictions Settings</h3>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_closed_to_arrival' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Click Through Arival: </label>
                                <div className='col-sm-8'>
                                    {/* Checkbox */}
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_closed_to_arrival'
                                            {...register('inherit_closed_to_arrival')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_closed_to_arrival'></label>
                                    </div>
                                </div>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_closed_to_departure' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Click Through Departure: </label>
                                <div className='col-sm-8'>
                                    {/* Checkbox */}
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_closed_to_departure'
                                            {...register('inherit_closed_to_departure')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_closed_to_departure'></label>
                                    </div>
                                </div>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_stop_sell' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Stop Sell: </label>
                                <div className='col-sm-8'>
                                    {/* Checkbox */}
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_stop_sell'
                                            {...register('inherit_stop_sell')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_stop_sell'></label>
                                    </div>
                                </div>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_min_stay_arrival' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Min Stay Arrival: </label>
                                <div className='col-sm-8'>
                                    {/* Checkbox */}
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_min_stay_arrival'
                                            {...register('inherit_min_stay_arrival')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_min_stay_arrival'></label>
                                    </div>
                                </div>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_min_stay_through' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Min Stay Through: </label>
                                <div className='col-sm-8'>
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_min_stay_through'
                                            {...register('inherit_min_stay_through')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_min_stay_through'></label>
                                    </div>
                                </div>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='inherit_max_stay' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Max Stay: </label>
                                <div className='col-sm-8'>
                                    <div className='form-check form-switch'>
                                        <input
                                            className='form-check-input'
                                            type='checkbox'
                                            id='inherit_max_stay'
                                            {...register('inherit_max_stay')}
                                        />
                                        <label className='form-check-label' htmlFor='inherit_max_stay'></label>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <h3 className="mt-6 border-bottom fw-normal">Rate Price Settings</h3>
                    {/* extra_adult_rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_adult_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="extra_adult_rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.amount?.message === 'string' ? errors.amount.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter extra adult rate in Rupiah"
                                        disabled={inheritRestriction} // Nonaktifkan input jika inheritRestriction true
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* extra_child_rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_child_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="extra_child_rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.extra_child_rate?.message === 'string' ? errors.extra_child_rate.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter extra child rate in Rupiah"
                                        disabled={inheritRestriction} // Nonaktifkan input jika inheritRestriction true
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <h3 className="mt-6 border-bottom fw-normal">Link Rate Settings</h3>
                    <div className='row mb-3 align-items-center'>
                        <label htmlFor='specific_rule' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Rule: </label>
                        <div className='col-sm-8'>
                            <Controller
                                name='specific_rule'
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={LinkRateSpecificRule}
                                        className={clsx('app-form-control', errors.specific_rule && 'is-invalid')}
                                        placeholder='Select specific rule'
                                        onChange={(selectedOption) => field.onChange(selectedOption)}
                                    />
                                )}
                            />
                            {errors.specific_rule && (
                                <div className="invalid-feedback d-block">{typeof errors.specific_rule.message === 'string' && errors.specific_rule.message}</div>
                            )}
                        </div>
                    </div>

                    <div className='row mb-3 align-items-center'>
                        <label htmlFor='specific_amount_type' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Amount Type: </label>
                        <div className='col-sm-8'>
                            <Controller
                                name='specific_amount_type'
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={LinkRateSpecificAmounType}
                                        className={clsx('app-form-control', errors.specific_amount_type && 'is-invalid')}
                                        placeholder='Select specific amount type'
                                        onChange={(selectedOption) => field.onChange(selectedOption)}
                                    />
                                )}
                            />
                            {errors.specific_amount_type && (
                                <div className="invalid-feedback d-block">{typeof errors.specific_amount_type.message === 'string' && errors.specific_amount_type.message}</div>
                            )}
                        </div>
                    </div>

                    <div className='row mb-3 align-items-center'>
                        <label htmlFor='specific_value' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Specific Value: </label>
                        <div className='col-sm-8'>
                            {watch('specific_amount_type')?.value === 'percent' ? (
                                <input
                                    type='number'
                                    className={clsx('form-control app-form-control rounded-1', errors.specific_value && 'is-invalid')}
                                    id='specific_value'
                                    placeholder='Enter specific value'
                                    {...register('specific_value')}
                                />
                            ) : (
                                <Controller
                                    name='specific_value'
                                    control={control}
                                    render={({ field }) => (
                                        <CurrencyInput
                                            {...field}
                                            value={field.value}
                                            onChange={(e, rawValue) => {
                                                e.preventDefault();
                                                field.onChange(rawValue);
                                            }}
                                            error={typeof errors.specific_value?.message === 'string' ? errors.specific_value.message : undefined}
                                            placeholder='Enter specific value in Rupiah'
                                        />
                                    )}
                                />
                            )}
                            {errors.specific_value && (
                                <div className='invalid-feedback'>{typeof errors.specific_value.message === 'string' && errors.specific_value.message}</div>
                            )}
                        </div>
                    </div>

                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            {isPending ? 'Creating...' : 'Create Link Rateplan'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateLinkRateDialog